import { Fragment, useEffect, useState, type ReactNode } from 'react';
import {
  CI_REGION, RESERVED_IMPACT, RESERVED_REGION,
  type Attribution, type CheckStatus, type HoldState, type RegionControl, type Reservation, type ScheduledReservation,
  type ServiceState, type WorkspaceState,
} from '@vinz/shared';
import { useAuth } from '../auth.js';
import { formatAgo, formatDuration, formatLeft, formatLocal, viewerTimeZone } from '../lib/format.js';
import {
  allowedActions, DEFAULT_EXTEND_HOURS, expiredUnreleased, EXTEND_HOURS, type ConsoleAction, type ConsoleOutcome, type ConsolePermissions,
} from '../console-actions.js';
import { useConsoleAction, usePermissionsQuery, useStartSweep, useStateQuery, type RunConsoleAction } from '../use-api.js';

const GITHUB_ORG = 'rewindio';
const BOT_CREATOR = 'github-actions[bot]';

function commitUrl(repo: string, sha: string): string {
  return `https://github.com/${GITHUB_ORG}/${repo}/commit/${sha}`;
}

/** Every link on this page leaves it for GitHub; none should take the dashboard with it. */
export function ExtLink({ href, className, title, children }: {
  href: string; className?: string; title?: string; children: ReactNode;
}) {
  return (
    <a href={href} className={className} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const ROW_CLASS: Record<HoldState, string> = {
  free: '',
  held: 'held',
  restoring: 'restoring',
  restore_failed: 'failed',
};

/**
 * The one workspace a collapsed row speaks for: the one whose SHA most observed workspaces share, so
 * on a half-applied deploy the row shows what the majority is running and the service cell flags the
 * rest. `workspaces[0]` is position 0 of the CONFIGURED list, observed or not, so it is never used
 * blindly; when nothing has reported there is genuinely nothing to show.
 */
export function representativeWorkspace(workspaces: WorkspaceState[]): WorkspaceState | undefined {
  const observed = workspaces.filter((w) => w.sha !== undefined);
  const counts = new Map<string, number>();
  for (const w of observed) counts.set(w.sha as string, (counts.get(w.sha as string) ?? 0) + 1);
  let best: WorkspaceState | undefined;
  for (const w of observed) {
    if (!best || (counts.get(w.sha as string) ?? 0) > (counts.get(best.sha as string) ?? 0)) best = w;
  }
  return best;
}

const CONTROL_LABEL: Record<RegionControl, string> = { on: 'Vinz live', off: 'Vinz off', unset: 'Vinz not set up' };
const CONTROL_TITLE: Record<RegionControl, string> = {
  on: 'Vinz control is on: /reserve is accepted and deploys honour a hold',
  off: 'Vinz control is off: /reserve is refused, and deploys don\'t need a reservation',
  unset: 'Vinz control is not set for this repo: /reserve is refused, and deploys don\'t need a reservation',
};

/**
 * Go-live flips repos on one at a time, so which ones Vinz is actually live on is read off this line.
 * Only "on" is emphasised; nothing is shown until a poll has read the variable, rather than a guess.
 */
function RegionControlLine({ control }: { control?: RegionControl }) {
  if (!control) return null;
  return (
    <div className={control === 'on' ? 'sub tone-brand' : 'sub'} title={CONTROL_TITLE[control]}>
      {control === 'on' ? <strong>{CONTROL_LABEL.on}</strong> : CONTROL_LABEL[control]}
    </div>
  );
}

/** Reservable, but the labels sync strips an unlisted branch-deploy label, so branch deploys there are unreliable. */
function LabelMissingBadge({ labelMissing }: { labelMissing?: boolean }) {
  if (!labelMissing) return null;
  return (
    <div className="sub">
      <span className="pill-flag" title="This repo’s .github/labels.yml has no branch-deploy label, so the sync may delete it and branch deploys cannot be relied on. It is still reservable.">
        label missing
      </span>
    </div>
  );
}

/** The reserved region, kept on one line: its hyphens are break points, and "us-" / "east-2" reads as two things. */
const RESERVED = <span className="nowrap">{RESERVED_REGION}</span>;
const CI = <span className="nowrap">{CI_REGION}</span>;

/**
 * A branch left in CI's region (vinz#114) - by a dispatch made while the repo's control was off, say. Every other cell
 * of the row describes the reserved region, so the pill and its line always name CI's region, never just "a branch".
 * Shown only for a reading this page can still refresh; `main` and `unseen` show nothing.
 */
function CiRegionNotice({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  const ci = svc.ciRegion;
  if (svc.check.state !== 'current' || ci?.state !== 'branch') return null;
  const ref = ci.runUrl ? <a href={ci.runUrl} target="_blank" rel="noreferrer" className="mono">{ci.ref}</a>
    : <span className="mono">{ci.ref}</span>;
  return (
    <div className="ci-region">
      <span className="pill pill-yellow">{CI_REGION} on a branch</span>
      <div className="sub">
        {CI} is running {ref}
        {ci.actor || ci.deployedAt ? ` (${[ci.actor ? `@${ci.actor}` : '', ci.deployedAt ? formatAgo(ci.deployedAt, nowMs) : '']
          .filter(Boolean).join(', ')})` : ''}
        {ci.pr ? <> · <a href={ci.pr.url} target="_blank" rel="noreferrer">#{ci.pr.number}</a></> : null}
        . CI’s region should run <span className="mono">main</span>; the next main deploy replaces it.
      </div>
    </div>
  );
}


const RUNTIME_TITLE: Record<NonNullable<ServiceState['runtime']>, string> = { ecs: 'ECS', lambda: 'Lambda' };

/**
 * What the service runs on, as a glyph before its name: a box for ECS containers, λ for Lambda. The
 * word is on hover and read to screen readers. Nothing for a service with no shared deploy workflow.
 */
function RuntimeIcon({ runtime }: { runtime?: ServiceState['runtime'] }) {
  if (!runtime) return null;
  return (
    <span className={`runtime runtime-${runtime}`} title={RUNTIME_TITLE[runtime]} role="img" aria-label={RUNTIME_TITLE[runtime]}>
      {runtime === 'ecs' ? (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
          <path d="M8 1.75 13.75 5v6L8 14.25 2.25 11V5z" />
          <path d="M2.25 5 8 8.25 13.75 5M8 8.25v6" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.5 2.5h1.75L11.5 13.5" />
          <path d="M8.1 6.9 4.25 13.5" />
        </svg>
      )}
    </span>
  );
}

/**
 * The repo name with a break opportunity after each underscore, so atlas_sns_lambda_forwarder wraps at
 * a word boundary the way hyphenated names already do, instead of splitting mid-word.
 */
function ServiceLabel({ repo }: { repo: string }) {
  const parts = repo.split('_');
  return (
    <span className="service-label">
      {parts.map((part, i) => (
        <Fragment key={i}>{part}{i < parts.length - 1 && <>_<wbr /></>}</Fragment>
      ))}
    </span>
  );
}

function ServiceCell({ svc }: { svc: ServiceState }) {
  // The count is of workspaces that actually reported; an unread one is named under What is deployed
  // instead of being folded into a number that implies the reading covers it.
  const observed = svc.workspaces.filter((w) => w.sha !== undefined).length;
  const total = observed > 0 ? observed : svc.workspaces.length;
  const rep = representativeWorkspace(svc.workspaces);
  const differ = svc.mixed && rep
    ? svc.workspaces.filter((w) => w.sha !== undefined && w.sha !== rep.sha).length
    : 0;
  // With no strict majority (1:1, 1:1:1) there is no "odd one out" to count, and the row's PR is just
  // one of them - say they disagree instead of implying which target is behind.
  const split = differ > 0 && differ >= observed - differ;
  // A single-target service - nearly all of them - needs no sub-line. Only a service that deploys as
  // several ECS services (restore-service: restore, restore-copy, restore-sync) says so, with the
  // names on hover, because that is what makes "N of M differ" mean something.
  if (total <= 1) {
    return (
      <>
        <div className="service-name"><RuntimeIcon runtime={svc.runtime} /><ServiceLabel repo={svc.repo} /></div>
        <RegionControlLine control={svc.regionControl} />
        <LabelMissingBadge labelMissing={svc.labelMissing} />
      </>
    );
  }
  return (
    <>
      <div className="service-name"><RuntimeIcon runtime={svc.runtime} /><ServiceLabel repo={svc.repo} /></div>
      <RegionControlLine control={svc.regionControl} />
      <LabelMissingBadge labelMissing={svc.labelMissing} />
      <div className="sub" title={svc.workspaces.map((w) => w.workspace).join('\n')}>
        {total} deploy targets
        {differ > 0 && (
          <> · <strong className="differ">{split ? 'targets disagree' : `${differ} of ${total} differ`}</strong></>
        )}
      </div>
    </>
  );
}

function WhoHasItCell({ holdState, reservation, releasedAt, retriedAt, check, reservable, reason, nowMs, expired }: {
  holdState: HoldState; reservation?: Reservation; releasedAt?: string; retriedAt?: string; check: CheckStatus;
  reservable: boolean; reason?: string; nowMs: number; expired: boolean;
}) {
  // Neither "Free" nor "Unknown" is true of a service Vinz cannot reserve: nobody can take it, so
  // the question the column answers does not apply. Say so, and why, so the fix is findable. A
  // service that lost its wiring mid-hold is still held, though - that hold, its holder and any
  // failed restore keep their normal treatment, with the reason added beneath.
  const notReservable = !reservable && reason ? <div className="sub">Not reservable: {reason}</div> : null;
  if (!reservable && holdState === 'free' && !expired) {
    return (
      <>
        <span className="pill pill-neutral">Not reservable</span>
        {reason && <div className="sub" style={{ marginTop: 7 }}>{reason}</div>}
      </>
    );
  }
  let pill: ReactNode;
  let detail: ReactNode;
  let impact: string | undefined;
  // Ran out but never released (read from the hold itself): one state for the whole row, whatever /state called it.
  switch (expired ? 'expired' : holdState) {
    case 'expired':
      pill = <span className="pill pill-yellow">Expired, not released</span>;
      detail = <>releasing it redeploys main to {RESERVED}</>;
      break;
    case 'held':
      pill = <span className="pill pill-brand">Held</span>;
      detail = reservation?.reason;
      impact = RESERVED_IMPACT;
      break;
    case 'restoring':
      // A retried restore is back in its grace: amber, not the failure it was, so nobody retries again.
      pill = <span className="pill pill-yellow">{retriedAt ? 'Restoring main (retried)' : 'Restoring main'}</span>;
      detail = retriedAt ? 'restore retried, environment being reset' : 'hold ended, environment being reset';
      break;
    case 'restore_failed':
      pill = <span className="pill pill-red">Restore failed</span>;
      detail = reservation ? `hold ended ${formatAgo(releasedAt ?? reservation.expires_at, nowMs)}` : undefined;
      break;
    case 'free':
    default:
      // "Free" tells someone they can deploy, so it needs a reading behind it. When holds could not
      // be read - the poll is failing or stale, or has never succeeded - there may be a hold nobody
      // can see, and saying Free would invite deploying over it.
      if (check.state === 'cannot_check' || !check.lastSuccessAt) {
        return (
          <>
            <span className="pill pill-neutral">Unknown</span>
            <div className="sub" style={{ marginTop: 7 }}>Holds can’t be read right now</div>
          </>
        );
      }
      // An unknown future state reads as free rather than borrowing another state's alarm.
      return (
        <>
          <span className="pill pill-green">Free</span>
          <div className="sub" style={{ marginTop: 7 }}>Nobody — anyone can deploy</div>
        </>
      );
  }
  return (
    <>
      {pill}
      {reservation && <div className="holder">{reservation.holder}</div>}
      {detail && <div className="sub">{detail}</div>}
      {impact && <div className="sub">{impact}</div>}
      {notReservable}
    </>
  );
}

function prLabel(url: string): string {
  const n = /\/pull\/(\d+)/.exec(url)?.[1];
  return n ? `#${n}` : 'PR';
}

/**
 * Holds promised for later (`/reserve from …`), soonest first. Information only: a schedule is not a
 * hold, so nothing else in the row - its colour, pill or any other cell - changes because of it.
 */
function ScheduledLines({ scheduled }: { scheduled?: ScheduledReservation[] }) {
  if (!scheduled || scheduled.length === 0) return null;
  return (
    <div className="scheduled">
      {scheduled.map((s) => (
        <div className="sub" key={`${s.pr}#${s.startAt}`}>
          <strong>Scheduled</strong> · {s.holder}{' '}
          {s.status === 'starting' ? 'starting now' : `from ${formatLocal(s.startAt)}`}
          {` for ${formatDuration((Date.parse(s.endAt) - Date.parse(s.startAt)) / 1000)}`}
          {s.reason ? ` — ${s.reason}` : ''} · <ExtLink href={s.pr}>{prLabel(s.pr)}</ExtLink>
        </div>
      ))}
    </div>
  );
}

/** What the page needs to offer console actions. Absent (or permissions unknown) means no buttons. */
export interface ConsoleControls {
  perms: ConsolePermissions | undefined;
  run: RunConsoleAction;
}

const PENDING_LABEL: Record<ConsoleAction, string> = {
  extend: 'Extending…',
  release: 'Releasing…',
  retry_restore: 'Retrying…',
  nudge: 'Asking…',
  notify: 'Saving…',
};

/**
 * Extend / Release / Retry restore / Nudge / Notify me from the console. Which buttons show is the worker's
 * answer about the viewer - their own holds, and whether they are cloudops - never a guess from the
 * holder's name: a GitHub handle and a Google email do not compare. With no answer, no buttons.
 * Someone else's live hold offers only Nudge, which asks them and never takes it, and Notify me, a one-off
 * Slack DM when it ends (Release too for cloudops).
 */
export function HoldActions({ svc, actions, nowMs = Date.now() }: { svc: ServiceState; actions?: ConsoleControls; nowMs?: number }) {
  const [hours, setHours] = useState<number>(DEFAULT_EXTEND_HOURS);
  const [pending, setPending] = useState<ConsoleAction | null>(null);
  const [outcome, setOutcome] = useState<ConsoleOutcome | null>(null);
  // The last request whose outcome is unknown. Retrying the same action (and duration) on the same hold resends
  // its id, so a first attempt that did land is not applied a second time. A newer hold gets a fresh id.
  const [unsettled, setUnsettled] = useState<
    { action: ConsoleAction; durationMs?: number; holdId?: string; requestId: string } | null>(null);
  const allowed = allowedActions(svc, actions?.perms, nowMs);
  if (!actions || !(allowed.extend || allowed.release || allowed.retry || allowed.nudge || allowed.notify)) return null;

  const run = async (action: ConsoleAction) => {
    // Cloudops releasing somebody else's hold takes it from them, so it asks first.
    if (action === 'release' && !allowed.own
        && !window.confirm(`Release ${svc.reservation ? `${svc.reservation.holder}'s` : 'this'} hold on ${svc.repo}? main redeploys.`)) return;
    const durationMs = action === 'extend' ? hours * 3_600_000 : undefined;
    const requestId = unsettled && unsettled.action === action && unsettled.durationMs === durationMs
      && unsettled.holdId === svc.holdId ? unsettled.requestId : crypto.randomUUID();
    setPending(action);
    setOutcome(null);
    try {
      const result = await actions.run(requestId, action, svc, durationMs);
      setUnsettled(result.settled ? null : { action, durationMs, holdId: svc.holdId, requestId });
      setOutcome(result);
    } finally {
      setPending(null);
    }
  };

  const busy = pending !== null;
  return (
    <>
      <div className="acts">
        {allowed.extend && (
          // The duration and the button that uses it read as one control, so a picked 24h can't be
          // mistaken for a separate setting - the button names what it will do.
          <div className="act-group">
            <div className="seg" role="group" aria-label="Extend by">
              {EXTEND_HOURS.map((h) => (
                <button key={h} type="button" className="seg-opt" aria-pressed={hours === h} disabled={busy}
                  onClick={() => setHours(h)}>{h}h</button>
              ))}
            </div>
            <button type="button" className="act act-primary" disabled={busy} onClick={() => void run('extend')}>
              {pending === 'extend' ? PENDING_LABEL.extend : `Extend ${hours}h`}
            </button>
          </div>
        )}
        {allowed.release && (
          <button type="button" className="act act-secondary act-danger" disabled={busy} onClick={() => void run('release')}>
            {pending === 'release' ? PENDING_LABEL.release : 'Release now'}
          </button>
        )}
        {allowed.retry && (
          <button type="button" className="act act-secondary" disabled={busy} onClick={() => void run('retry_restore')}>
            {pending === 'retry_restore' ? PENDING_LABEL.retry_restore : 'Retry restore'}
          </button>
        )}
        {allowed.nudge && (
          <button type="button" className="act act-secondary" disabled={busy} onClick={() => void run('nudge')}>
            {pending === 'nudge' ? PENDING_LABEL.nudge : `Nudge @${svc.reservation?.holder}`}
          </button>
        )}
        {allowed.notify && (
          <button type="button" className="act act-secondary" disabled={busy} onClick={() => void run('notify')}>
            {pending === 'notify' ? PENDING_LABEL.notify : 'Notify me when free'}
          </button>
        )}
      </div>
      {outcome && <div role="status" className={`sub ${outcome.ok ? '' : 'tone-red'}`}>{outcome.message}</div>}
    </>
  );
}

function ForHowLongCell({ svc, nowMs, actions }: { svc: ServiceState; nowMs: number; actions?: ConsoleControls }) {
  const { holdState, reservation } = svc;
  if (reservation && expiredUnreleased(svc, nowMs)) {
    return (
      <>
        <div className="time-left tone-yellow">Expired {formatAgo(reservation.expires_at, nowMs)}</div>
        <div className="sub">never released · releasing redeploys main</div>
        <HoldActions svc={svc} actions={actions} nowMs={nowMs} />
      </>
    );
  }
  switch (holdState) {
    case 'held': {
      if (!reservation) return <span className="muted">—</span>;
      const grantedMs = Date.parse(reservation.granted_at);
      const expiresMs = Date.parse(reservation.expires_at);
      const totalMs = expiresMs - grantedMs;
      const pct = Math.min(100, Math.max(0, totalMs > 0 ? ((nowMs - grantedMs) / totalMs) * 100 : 100));
      const extended = reservation.extensions > 0 ? `extended ${reservation.extensions}×` : 'not extended';
      return (
        <>
          <div className="time-left tone-brand">{formatLeft(reservation.expires_at, nowMs)}</div>
          <div className="sub">until {formatLocal(reservation.expires_at)} · {extended}</div>
          <div className="bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
            <i style={{ width: `${pct}%` }} />
          </div>
          <HoldActions svc={svc} actions={actions} nowMs={nowMs} />
        </>
      );
    }
    case 'restoring':
      return (
        <>
          {svc.restoreRetriedAt ? (
            <>
              <div className="time-left tone-yellow">Retried {formatAgo(svc.restoreRetriedAt, nowMs)}</div>
              <div className="sub">main redeploying{svc.restoreRetriedBy ? ` · from the Vinz console by ${svc.restoreRetriedBy}` : ''}</div>
            </>
          ) : (
            <>
              {reservation && (
                <div className="time-left tone-yellow">
                  {svc.releasedAt ? `Released ${formatAgo(svc.releasedAt, nowMs)}` : `Expired ${formatAgo(reservation.expires_at, nowMs)}`}
                </div>
              )}
              <div className="sub">main redeploying automatically</div>
            </>
          )}
          <HoldActions svc={svc} actions={actions} nowMs={nowMs} />
        </>
      );
    case 'restore_failed':
      return (
        <>
          <div className="time-left tone-red">Restore failed</div>
          <div className="sub tone-red">
            {svc.restoreRetriedAt
              ? `the retry ${formatAgo(svc.restoreRetriedAt, nowMs)} did not complete either — still on their branch`
              : 'redeploy of main did not complete — still on their branch'}
          </div>
          <HoldActions svc={svc} actions={actions} nowMs={nowMs} />
        </>
      );
    case 'free':
    default:
      // A free row offers nothing (an expired, unreleased hold is handled above; see expiredUnreleased).
      return (
        <>
          <span className="muted">—</span>
          <HoldActions svc={svc} actions={actions} nowMs={nowMs} />
        </>
      );
  }
}

/**
 * What is landing now, ahead of what landed last: without it the row goes on showing the previous deploy
 * for the minutes a deploy takes, and "is my branch there yet?" gets the wrong answer.
 */
function DeployingLine({ svc }: { svc: ServiceState }) {
  const d = svc.deploying;
  if (!d) return null;
  const was = representativeWorkspace(svc.workspaces);
  const replacing = was ? (was.pr ? `#${was.pr.number}` : was.ref) : undefined;
  return (
    <>
      <div className="pr-line">
        <span className="pill pill-yellow">Deploying</span>{' '}
        <ExtLink href={d.runUrl} className="ref" title={`${d.ref} @ ${d.sha.slice(0, 8)}`}>
          {d.pr ? <><strong>#{d.pr.number}</strong> {d.pr.title}</> : d.ref || d.sha.slice(0, 8)}…
        </ExtLink>
      </div>
      {replacing && <div className="sub">replacing {replacing}</div>}
    </>
  );
}

/**
 * A hold granted after the deploy the row shows, with its branch deploy not seen yet. Vinz dispatches one
 * on every grant, so the deploy on show is the one about to be replaced: showing it beside the new hold
 * would pair one person's reservation with another deploy's branch and author. The row says it is waiting.
 */
export function awaitingDeploy(svc: ServiceState): boolean {
  if (svc.holdState !== 'held' || !svc.reservation || svc.deploying) return false;
  // Nothing observed at all is "no reading yet", not a deploy being replaced.
  const shown = representativeWorkspace(svc.workspaces);
  if (!shown?.deployedAt) return false;
  return Date.parse(shown.deployedAt) < Date.parse(svc.reservation.granted_at);
}

/**
 * A grant's branch deploy starts within seconds. Past this, "waiting" would be a claim with nothing behind
 * it - a failed dispatch would otherwise read as waiting for the whole hold - so the row says what it
 * actually knows: no deploy has been seen.
 */
export const AWAIT_DEPLOY_MS = 10 * 60_000;

function AwaitingLine({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  const was = representativeWorkspace(svc.workspaces);
  const wasRef = was ? (was.pr ? `#${was.pr.number}` : was.ref) : undefined;
  const grantedMs = svc.reservation ? Date.parse(svc.reservation.granted_at) : nowMs;
  if (nowMs - grantedMs > AWAIT_DEPLOY_MS) {
    const pr = svc.reservation?.pr;
    return (
      <>
        <div className="pr-line"><span className="pill pill-yellow">No branch deploy seen</span></div>
        <div className="sub">
          {pr ? <ExtLink href={pr}>check the run</ExtLink> : 'check the run'}
          {` · granted ${formatAgo(svc.reservation?.granted_at ?? '', nowMs)}`}{wasRef ? ` · still ${wasRef}` : ''}
        </div>
      </>
    );
  }
  return (
    <>
      <div className="pr-line"><span className="pill pill-yellow">Waiting</span> for the branch deploy…</div>
      {wasRef && <div className="sub">was {wasRef}</div>}
    </>
  );
}

function attributionName(attribution: Attribution) {
  return attribution.kind === 'person' ? <div>{attribution.name}</div>
    : attribution.kind === 'ci' ? <div><span className="muted" title={`last main deploy by ${attribution.name}`}>CI</span></div>
    : <div><span className="muted">Vinz · {attribution.name === 'branch deploy' ? 'branch deploy' : 'auto-restore'}</span></div>;
}

function DeployedCell({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  if (svc.deploying) return <DeployingLine svc={svc} />;
  if (awaitingDeploy(svc)) return <AwaitingLine svc={svc} nowMs={nowMs} />;
  const rep = representativeWorkspace(svc.workspaces);
  if (!rep) return <span className="muted">—</span>;
  // representativeWorkspace only ever returns a workspace with a SHA.
  const { ref, pr } = rep;
  const sha = rep.sha as string;
  const unobserved = svc.workspaces.filter((w) => w.sha === undefined).map((w) => w.workspace);
  // The PR says what is being tested; the branch is only the fallback for a deploy with no PR (main,
  // or a branch nobody opened one for). The SHA stays reachable on hover rather than taking a line.
  const detail = [ref, sha.slice(0, 8)].filter(Boolean).join(' @ ');
  return (
    <>
      {pr ? (
        <div className="pr-line">
          <ExtLink href={pr.url} className="ref" title={detail}>
            <strong>#{pr.number}</strong> {pr.title}
          </ExtLink>
          {pr.merged && <span className="pill-merged">merged</span>}
        </div>
      ) : (
        ref && (
          <ExtLink href={commitUrl(svc.repo, sha)} className="branch" title={detail}>
            {ref}
          </ExtLink>
        )
      )}
      {/* A workspace that has never deployed, not two workspaces disagreeing: the steady state for a
          service that does not use all its configured workspaces, so it is quiet - but still said,
          because the row otherwise implies its reading covers workspaces it does not. */}
      {unobserved.length > 0 && <div className="sub">no reading yet: {unobserved.join(', ')}</div>}
    </>
  );
}

function DeployedByCell({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  // The same deploy as the Deployed cell, always: the one in flight, else nothing while the new hold's
  // deploy is awaited, else the one that landed.
  const d = svc.deploying;
  if (d) {
    return (
      <>
        {d.attribution ? attributionName(d.attribution) : <div>{d.actor}</div>}
        <div className="sub">started <ExtLink href={d.runUrl}>{formatAgo(d.startedAt, nowMs)}</ExtLink></div>
      </>
    );
  }
  if (awaitingDeploy(svc)) return <span className="muted">—</span>;
  const w = representativeWorkspace(svc.workspaces);
  if (!w) return <span className="muted">—</span>;
  return (
    <>
      {attributionName(w.attribution)}
      {w.actorIsBot && <div className="sub">via {BOT_CREATOR}</div>}
      {w.deployedAt && (
        <div className="sub">
          {w.runUrl ? <ExtLink href={w.runUrl}>{formatAgo(w.deployedAt, nowMs)}</ExtLink> : formatAgo(w.deployedAt, nowMs)}
        </div>
      )}
    </>
  );
}

function CheckedCell({ check, nowMs }: { check: CheckStatus; nowMs: number }) {
  if (check.state === 'current') {
    return (
      <>
        <span className="pill pill-green">Current</span>
        {check.lastSuccessAt && <div className="sub">{formatAgo(check.lastSuccessAt, nowMs)}</div>}
      </>
    );
  }
  if (check.state === 'cannot_check') {
    const lastRead = check.lastSuccessAt ? `last read ${formatAgo(check.lastSuccessAt, nowMs)}` : 'no successful read yet';
    return (
      <>
        <span className="pill pill-yellow">Can’t check</span>
        <div className="sub">{check.reason ? `${lastRead} · ${check.reason}` : lastRead}</div>
      </>
    );
  }
  return (
    <>
      <span className="pill pill-neutral">Never checked</span>
      <div className="sub">no successful read yet</div>
    </>
  );
}

export interface StatusTableProps {
  services: ServiceState[];
  nowMs: number;
  actions?: ConsoleControls;
}

export function StatusTable({ services, nowMs, actions }: StatusTableProps) {
  return (
    <table className="grid">
      <thead>
        <tr>
          <th scope="col" style={{ width: '14%' }}>Service</th>
          <th scope="col" style={{ width: '19%' }}>Who has it</th>
          <th scope="col" style={{ width: '17%' }}>For how long</th>
          <th scope="col" style={{ width: '25%' }}>What is deployed</th>
          <th scope="col" style={{ width: '13%' }}>Deployed by</th>
          <th scope="col" style={{ width: '12%' }}>Checked</th>
        </tr>
      </thead>
      <tbody>
        {services.map((svc) => { const expired = expiredUnreleased(svc, nowMs); return (
          <tr key={svc.repo} className={expired ? '' : ROW_CLASS[svc.holdState] ?? ''}>
            <td><ServiceCell svc={svc} /></td>
            <td><WhoHasItCell holdState={svc.holdState} reservation={svc.reservation} releasedAt={svc.releasedAt}
              retriedAt={svc.restoreRetriedAt} check={svc.check}
              reservable={svc.reservable} reason={svc.reason} nowMs={nowMs} expired={expired} />
              <CiRegionNotice svc={svc} nowMs={nowMs} />
              <ScheduledLines scheduled={svc.scheduled} /></td>
            <td><ForHowLongCell svc={svc} nowMs={nowMs} actions={actions} /></td>
            <td><DeployedCell svc={svc} nowMs={nowMs} /></td>
            <td><DeployedByCell svc={svc} nowMs={nowMs} /></td>
            <td><CheckedCell check={svc.check} nowMs={nowMs} /></td>
          </tr>
        ); })}
      </tbody>
    </table>
  );
}

/** The newest successful sweep across services - what "last sweep" means, not any row's observedAt. */
/**
 * Whether a forced sweep requested at `since` has reached every row. The poller writes each service's
 * reading as it gets to it, so the newest timestamp (lastSweep) moves as soon as the FIRST service is
 * done - treating that as "landed" stopped the fast polling while later rows still showed the old age.
 * Only services currently readable are waited for: one the poller cannot check never gets a newer
 * lastSuccessAt, and the SWEEP_WAIT_MS cap still bounds the wait.
 */
export function sweepLanded(services: ServiceState[], since: string): boolean {
  const readable = services.filter((s) => s.check.state === 'current');
  return readable.length > 0 && readable.every((s) => (s.check.lastSuccessAt ?? '') >= since);
}

function lastSweep(services: ServiceState[]): string | undefined {
  return services
    .map((s) => s.check.lastSuccessAt)
    .filter((t): t is string => t !== undefined)
    .sort()
    .at(-1);
}

function CannotCheckBanner({ services }: { services: ServiceState[] }) {
  const stuck = services.filter((s) => s.check.state === 'cannot_check').map((s) => s.repo);
  if (stuck.length === 0) return null;
  const names = stuck.join(', ');
  return (
    <div className="banner-warning" role="status">
      <span aria-hidden="true" className="banner-mark">!</span>
      <div>
        <strong>Vinz can’t reach GitHub for {names}.</strong> This is the last reading it managed.{' '}
        <em>{stuck.length === 1 ? `${names} itself is fine` : 'The services themselves are fine'}</em> — it is this page that has
        stopped being refreshed.
      </div>
    </div>
  );
}


/** Give up waiting for a forced sweep to land after this; the scheduled refresh takes over again. */
export const SWEEP_WAIT_MS = 90 * 1000;

/**
 * Starts a sweep now rather than waiting for the next five-minute one. The API rate-limits this to one
 * a minute across everybody, so a refusal is an ordinary answer, shown as when the last one started.
 */
function SweepControl({ idToken, sweeping, onStarted, nowMs }: {
  idToken: string | null; sweeping: boolean; onStarted: (requestedAt: string) => void; nowMs: number;
}) {
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const startSweep = useStartSweep();

  async function sweepNow() {
    if (!idToken) return;
    setSending(true);
    setNote(null);
    try {
      const body = await startSweep(idToken);
      if (body.status === 'started') {
        onStarted(body.requestedAt);
      } else {
        const last = body.lastRequestedAt ? `swept ${formatAgo(body.lastRequestedAt, nowMs)} · ` : '';
        setNote(`${last}try again in ${body.retryAfterSeconds}s`);
      }
    } catch (err) {
      // Kept visible in devtools: the note below cannot say whether it was the network, auth or a 502.
      console.error('sweep failed', err);
      setNote((err as Error)?.name === 'UnauthorizedError' ? 'session expired — signing you back in, try again' : 'couldn’t start a sweep');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`sweep-btn${sweeping ? ' spinning' : ''}`}
        onClick={() => void sweepNow()}
        disabled={sweeping || sending}
        aria-label={sweeping ? 'Sweeping…' : 'Sweep now'}
        title={sweeping ? 'Sweeping…' : 'Sweep now — check GitHub for new deploys'}
      >
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
          <path d="M13.5 2.5v3h-3" />
        </svg>
      </button>
      {note && <div className="sweep-note" role="status">{note}</div>}
    </>
  );
}

export function Status() {
  const { idToken } = useAuth();
  const [sweepingSince, setSweepingSince] = useState<string | null>(null);
  const { data, isPending, isError, error } = useStateQuery(idToken, { sweeping: sweepingSince !== null });
  const perms = usePermissionsQuery(idToken);
  const run = useConsoleAction(idToken);
  const landed = data ? lastSweep(data.services) : undefined;
  const sweepDone = data && sweepingSince ? sweepLanded(data.services, sweepingSince) : false;

  // A forced sweep is over when a newer successful sweep shows up, or after SWEEP_WAIT_MS regardless.
  useEffect(() => {
    if (sweepDone) setSweepingSince(null);
  }, [sweepDone]);
  useEffect(() => {
    if (!sweepingSince) return;
    const t = setTimeout(() => setSweepingSince(null), SWEEP_WAIT_MS);
    return () => clearTimeout(t);
  }, [sweepingSince]);

  if (isPending) return <div className="card">Loading services…</div>;
  if (isError) {
    return <div className="card">Couldn’t load service state: {error instanceof Error ? error.message : String(error)}</div>;
  }

  const nowMs = Date.now();
  const sweep = landed;
  return (
    <div>
      <div className="page-head">
        <div>
          <h2>Staging {RESERVED_REGION}</h2>
          <p>One row per service, in alphabetical order. A service that deploys to several targets collapses to one row while they agree.</p>
        </div>
        <div className="page-head-meta">
          <div className="sweep-line">
            <span>Checked every 5 min{sweep && <> · last sweep {formatAgo(sweep, nowMs)}</>}</span>
            <SweepControl idToken={idToken} sweeping={sweepingSince !== null} onStarted={setSweepingSince} nowMs={nowMs} />
          </div>
          <div>Times in {viewerTimeZone()}</div>
        </div>
      </div>
      <CannotCheckBanner services={data.services} />
      <div className="table-card">
        <StatusTable services={data.services} nowMs={nowMs} actions={{ perms: perms.data, run }} />
      </div>
      <p className="page-note">
        <strong>Expiry restores the environment.</strong> When a hold ends, Vinz redeploys <span className="mono">main</span> to
        that workspace automatically, so a service is never left free-to-take but still running someone’s branch. A free
        service on <span className="mono">main</span> reads <strong>CI</strong> rather than a person — a name there would
        imply somebody has it. A free service on a <em>branch</em> still names the person, because that is the anomaly.
      </p>
    </div>
  );
}
